/**
 * LessonEngagement.js
 *
 * Interactive lesson widgets that plug into the lesson viewer:
 *   - ChecklistSection : renders section.checklist_data, saves state per-user
 *   - PollSection      : renders section.poll_data, votes + live results
 *   - AssignmentPanel  : list of assignments on the lesson + student submit + admin grade
 *   - DiscussionPanel  : threaded discussion on the lesson
 *
 * All calls hit /api endpoints created in server.py.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  CheckCircle2, Circle, BarChart3, MessageCircle, Reply, Trash2,
  Send, FileText, Clock, Award, Plus, UploadCloud, Paperclip, Loader2, X,
} from 'lucide-react';

/* ------------------ CHECKLIST ------------------ */
export const ChecklistSection = ({ section, lessonId }) => {
  const { API } = useContext(AuthContext);
  const items = section?.checklist_data?.items || [];
  const storageKey = `lms-checklist:${lessonId}:${section.id}`;
  // Seed from localStorage for instant paint, then reconcile with server.
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storageKey) || '[]')); }
    catch { return new Set(); }
  });

  // Server-side state fetch — reconciles across devices.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/lms/checklists/${section.id}/state`)
      .then((r) => {
        if (!alive) return;
        const ids = new Set(r.data?.checked_item_ids || []);
        setChecked(ids);
        try { localStorage.setItem(storageKey, JSON.stringify([...ids])); } catch { /* ignore */ }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [API, section.id, storageKey]);

  const toggle = async (id) => {
    const nowChecked = !checked.has(id);
    // Optimistic UI + localStorage
    setChecked(prev => {
      const next = new Set(prev);
      if (nowChecked) next.add(id); else next.delete(id);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
    // Persist server-side (best-effort — falls back to localStorage if it fails)
    try {
      await axios.post(`${API}/lms/checklists/${section.id}/toggle`, {
        item_id: id, checked: nowChecked,
      });
    } catch { /* silent — localStorage is source of truth on failure */ }
  };

  if (!items.length) {
    return <p className="text-sm text-muted-foreground italic">No checklist items configured.</p>;
  }
  const doneCount = items.filter(i => checked.has(i.id)).length;
  const pct = Math.round((doneCount / items.length) * 100);

  return (
    <div data-testid={`checklist-section-${section.id}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">{doneCount} of {items.length} complete</div>
        <span className={`status-pill ${pct === 100 ? 'status-pill-active' : 'status-pill-admin'}`}>
          {pct}%
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden mb-4">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="space-y-2">
        {items.map(item => {
          const done = checked.has(item.id);
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => toggle(item.id)}
                className="w-full flex items-start gap-3 text-left p-3 rounded-lg border border-border hover:border-primary/60 hover:bg-muted/50 transition"
                data-testid={`checklist-item-${item.id}`}
              >
                {done
                  ? <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />}
                <span className={done ? 'line-through text-muted-foreground' : 'text-foreground'}>
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/* ------------------ POLL ------------------ */
export const PollSection = ({ section, lessonId }) => {
  const { API } = useContext(AuthContext);
  const poll = section?.poll_data || {};
  const options = poll.options || [];
  const allowMultiple = !!poll.allow_multiple;
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState({ counts: {}, total_voters: 0, my_vote: [] });
  const [loading, setLoading] = useState(false);
  const [voted, setVoted] = useState(false);

  const fetchResults = async () => {
    try {
      const r = await axios.get(`${API}/lms/polls/${section.id}/results`);
      setResults(r.data);
      if ((r.data.my_vote || []).length) {
        setVoted(true);
        setSelected(new Set(r.data.my_vote));
      }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { fetchResults(); }, [section.id]);

  const toggle = (id) => {
    setSelected(prev => {
      if (allowMultiple) {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      }
      return new Set([id]);
    });
  };

  const submit = async () => {
    if (!selected.size) { toast.error('Choose at least one option'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/lms/polls/${section.id}/vote`, { option_ids: [...selected] });
      toast.success(voted ? 'Vote updated' : 'Vote recorded');
      setVoted(true);
      fetchResults();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record vote');
    } finally { setLoading(false); }
  };

  if (!poll.question || !options.length) {
    return <p className="text-sm text-muted-foreground italic">Poll not configured.</p>;
  }

  return (
    <div data-testid={`poll-section-${section.id}`}>
      <p className="font-semibold text-lg mb-4 text-foreground">{poll.question}</p>
      <div className="space-y-2">
        {options.map(opt => {
          const count = results.counts?.[opt.id] || 0;
          const pct = results.total_voters ? Math.round((count / results.total_voters) * 100) : 0;
          const isSelected = selected.has(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              className={`relative w-full text-left rounded-lg border p-3 transition overflow-hidden
                ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/40'}`}
              data-testid={`poll-option-${opt.id}`}
            >
              {voted && (
                <div className="absolute inset-y-0 left-0 bg-primary/10" style={{ width: `${pct}%` }} />
              )}
              <div className="relative flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-foreground">
                  {isSelected
                    ? <CheckCircle2 className="w-4 h-4 text-primary" />
                    : <Circle className="w-4 h-4 text-muted-foreground" />}
                  {opt.label}
                </span>
                {voted && (
                  <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                    {count} · {pct}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button
          size="sm" onClick={submit} disabled={loading}
          className="rounded-full font-semibold"
          data-testid="poll-submit-btn"
        >
          {voted ? 'Update vote' : 'Cast vote'} <Send className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" /> {results.total_voters} voter{results.total_voters === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
};

/* ------------------ ASSIGNMENTS ------------------ */
export const AssignmentPanel = ({ lessonId }) => {
  const { API, user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_date: '', max_score: 100 });

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lms/lessons/${lessonId}/assignments`);
      setItems(r.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAssignments(); }, [lessonId]);

  const createAssignment = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    try {
      await axios.post(`${API}/lms/lessons/${lessonId}/assignments`, {
        lesson_id: lessonId, ...form,
        due_date: form.due_date || null,
      });
      toast.success('Assignment created');
      setForm({ title: '', description: '', due_date: '', max_score: 100 });
      setShowCreate(false);
      fetchAssignments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const removeAssignment = async (id) => {
    if (!window.confirm('Delete this assignment and all submissions?')) return;
    try {
      await axios.delete(`${API}/lms/assignments/${id}`);
      toast.success('Assignment deleted');
      fetchAssignments();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return null;
  if (!isAdmin && items.length === 0) return null;

  return (
    <Card className="mb-6" data-testid="assignment-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> Assignments
        </CardTitle>
        {isAdmin && (
          <Button
            size="sm" variant="outline"
            onClick={() => setShowCreate(v => !v)}
            data-testid="assignment-toggle-create-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {showCreate ? 'Cancel' : 'New assignment'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin && showCreate && (
          <div className="border border-dashed border-border rounded-lg p-4 space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                data-testid="assignment-title-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                data-testid="assignment-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Due date (optional)</Label>
                <Input type="date" value={form.due_date}
                  onChange={e => setForm({ ...form, due_date: e.target.value })}
                  data-testid="assignment-due-input" />
              </div>
              <div>
                <Label className="text-xs">Max score</Label>
                <Input type="number" min={1} max={1000} value={form.max_score}
                  onChange={e => setForm({ ...form, max_score: Number(e.target.value) })}
                  data-testid="assignment-max-input" />
              </div>
            </div>
            <Button onClick={createAssignment} size="sm"
              className="rounded-full font-semibold" data-testid="assignment-create-btn">
              Create assignment
            </Button>
          </div>
        )}
        {items.length === 0 && isAdmin && !showCreate && (
          <p className="text-sm text-muted-foreground italic">No assignments yet — click &ldquo;New assignment&rdquo; to add one.</p>
        )}
        {items.map(a => (
          <AssignmentItem key={a.id} assignment={a} lessonId={lessonId} onDeleted={removeAssignment} />
        ))}
      </CardContent>
    </Card>
  );
};

const AssignmentItem = ({ assignment, lessonId, onDeleted }) => {
  const { API, user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  // "Grader" = platform admin OR the person who authored the assignment
  // (treated as course instructor). Matches the backend permission check.
  const isGrader = isAdmin || (user?.email && assignment.created_by === user.email);
  const [mine, setMine] = useState(null);
  const [content, setContent] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    if (isGrader) return;
    axios.get(`${API}/lms/assignments/${assignment.id}/my-submission`)
      .then(r => {
        if (r.data && r.data.id) {
          setMine(r.data);
          setContent(r.data.content || '');
          setFileUrl(r.data.file_url || '');
          setExternalUrl(r.data.external_url || '');
        }
      })
      .catch(() => {});
  }, [assignment.id]);

  const fetchSubs = async () => {
    try {
      const r = await axios.get(`${API}/lms/assignments/${assignment.id}/submissions`);
      setSubs(r.data || []);
    } catch { setSubs([]); }
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset input so re-selecting same file re-fires
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/uploads/assignment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFileUrl(r.data.url);
      setUploadedFileName(r.data.filename);
      toast.success('File uploaded — remember to click Submit');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!content.trim()) { toast.error('Write your submission first'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/lms/assignments/${assignment.id}/submit`, {
        content,
        file_url: fileUrl || null,
        external_url: externalUrl.trim() || null,
      });
      setMine(r.data);
      toast.success('Submission saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const gradeSubmission = async (subId, score, feedback) => {
    try {
      await axios.put(`${API}/lms/submissions/${subId}/grade`, {
        score: Number(score), feedback, status: 'graded',
      });
      toast.success('Grade saved');
      fetchSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grade');
    }
  };

  return (
    <div className="border border-border rounded-lg p-4" data-testid={`assignment-${assignment.id}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1">
          <div className="font-heading font-bold text-base text-foreground">{assignment.title}</div>
          {assignment.description && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{assignment.description}</p>
          )}
          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span>Max score: <strong className="text-foreground">{assignment.max_score}</strong></span>
            {assignment.due_date && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Due {assignment.due_date}</span>
            )}
          </div>
        </div>
        {isAdmin && (
          <Button size="sm" variant="ghost" onClick={() => onDeleted(assignment.id)}
            className="text-destructive" data-testid={`assignment-delete-${assignment.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Student view: submission form / status */}
      {!isGrader && (
        <div className="mt-3 space-y-2">
          <Label className="text-xs">Your submission</Label>
          <Textarea rows={4} value={content} onChange={e => setContent(e.target.value)}
            placeholder="Paste or type your response..."
            data-testid={`assignment-content-${assignment.id}`} />

          {/* Attachment — direct upload OR external URL. The upload button is
              the primary path; the URL field stays as a fallback for people
              who prefer sharing a Drive/Dropbox link. */}
          <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 hover:bg-primary/15 text-primary text-xs font-bold px-3 h-8 cursor-pointer transition-colors"
                data-testid={`assignment-upload-label-${assignment.id}`}>
                {uploading
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                  : <><UploadCloud className="w-3.5 h-3.5" /> Upload file</>}
                <input
                  type="file"
                  className="hidden"
                  disabled={uploading}
                  onChange={handleFilePick}
                  data-testid={`assignment-upload-input-${assignment.id}`}
                />
              </label>
              <span className="text-[11px] text-muted-foreground">PDF, image, doc, or video · up to 25MB</span>
            </div>
            {fileUrl && (
              <div className="flex items-center gap-2 text-xs" data-testid={`assignment-attachment-${assignment.id}`}>
                <Paperclip className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <a href={fileUrl} target="_blank" rel="noreferrer" className="text-primary underline truncate flex-1">
                  {uploadedFileName || fileUrl}
                </a>
                <button
                  type="button"
                  onClick={() => { setFileUrl(''); setUploadedFileName(''); }}
                  aria-label="Remove attachment"
                  className="h-5 w-5 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted flex items-center justify-center"
                  data-testid={`assignment-attachment-remove-${assignment.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {!fileUrl && (
              <Input
                value={fileUrl}
                onChange={e => setFileUrl(e.target.value)}
                placeholder="…or paste an external file URL (Drive, Dropbox, etc.)"
                data-testid={`assignment-file-${assignment.id}`}
                className="h-8 text-xs"
              />
            )}
          </div>

          {/* Optional external link — YouTube batch video, Loom walk-through,
              live-cook blog post. Kept separate from `file_url` so the
              grader sees an obvious "Watch / open link" button in the
              submission row. */}
          <div>
            <Label className="text-xs">External link (optional)</Label>
            <Input
              value={externalUrl}
              onChange={e => setExternalUrl(e.target.value)}
              placeholder="https://youtu.be/… or any URL"
              className="h-8 text-xs"
              data-testid={`assignment-external-url-${assignment.id}`}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button size="sm" onClick={submit} disabled={submitting || uploading}
              className="rounded-full font-semibold" data-testid={`assignment-submit-${assignment.id}`}>
              {mine ? 'Update submission' : 'Submit'}
            </Button>
            {mine && (() => {
              const passThreshold = Math.ceil(assignment.max_score * 0.6);
              const passed = mine.status === 'graded' && (mine.score ?? 0) >= passThreshold;
              const needsWork = mine.status === 'graded' && (mine.score ?? 0) < passThreshold;
              return (
                <div className="flex items-center gap-2 text-xs">
                  <span className={`status-pill ${
                    passed ? 'status-pill-active'
                    : needsWork ? 'status-pill-danger'
                    : 'status-pill-admin'
                  }`} data-testid={`assignment-status-pill-${assignment.id}`}>
                    {passed ? 'Pass' : needsWork ? 'Needs work' : mine.status}
                  </span>
                  {mine.status === 'graded' && (
                    <span className="text-muted-foreground">
                      Score: <strong className="text-foreground">{mine.score}/{assignment.max_score}</strong>
                      {mine.feedback && <> — “{mine.feedback}”</>}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Grader view: submissions list */}
      {isGrader && (
        <div className="mt-3">
          <Button size="sm" variant="outline"
            onClick={() => { setShowSubs(v => !v); if (!showSubs) fetchSubs(); }}
            data-testid={`assignment-toggle-subs-${assignment.id}`}>
            {showSubs ? 'Hide submissions' : 'View submissions'}
          </Button>
          {showSubs && (
            <div className="mt-3 space-y-3">
              {subs.length === 0 && <p className="text-sm text-muted-foreground italic">No submissions yet.</p>}
              {subs.map(s => (
                <SubmissionRow key={s.id} sub={s} maxScore={assignment.max_score}
                  onGrade={(score, fb) => gradeSubmission(s.id, score, fb)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SubmissionRow = ({ sub, maxScore, onGrade }) => {
  const [score, setScore] = useState(sub.score ?? '');
  const [feedback, setFeedback] = useState(sub.feedback || '');
  const gradedPass = sub.status === 'graded' && (sub.score ?? 0) >= Math.ceil(maxScore * 0.6);
  const gradedNeeds = sub.status === 'graded' && (sub.score ?? 0) < Math.ceil(maxScore * 0.6);
  return (
    <div className="rounded-lg border border-border p-3 bg-muted/30" data-testid={`submission-${sub.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">{sub.user_email}</div>
        <span className={`status-pill ${
          gradedPass ? 'status-pill-active'
          : gradedNeeds ? 'status-pill-danger'
          : 'status-pill-admin'
        }`}>
          {gradedPass ? 'Pass' : gradedNeeds ? 'Needs work' : sub.status}
        </span>
      </div>
      <div className="text-sm whitespace-pre-wrap text-foreground/90 mb-3">{sub.content}</div>
      {sub.file_url && (
        <a href={sub.file_url} target="_blank" rel="noreferrer" className="text-xs text-secondary underline mb-2 block">
          Attached file
        </a>
      )}
      {sub.external_url && (
        <a href={sub.external_url} target="_blank" rel="noreferrer"
           className="text-xs text-primary underline mb-3 block"
           data-testid={`submission-external-${sub.id}`}>
          External link: {sub.external_url}
        </a>
      )}

      {/* Two grading paths:
          1. Quick pass / needs-work pills — one click, no typing (matches
             the "simple pass/needs-work grading pill" spec).
          2. Numeric score + feedback for finer grading. */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Button size="sm" variant="outline"
          className="rounded-full font-semibold border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          onClick={() => onGrade(maxScore, feedback || 'Pass')}
          data-testid={`grade-pass-${sub.id}`}>
          <Award className="w-3.5 h-3.5 mr-1.5" /> Mark pass
        </Button>
        <Button size="sm" variant="outline"
          className="rounded-full font-semibold border-rose-500 text-rose-700 hover:bg-rose-50"
          onClick={() => onGrade(0, feedback || 'Needs work — please resubmit')}
          data-testid={`grade-needs-work-${sub.id}`}>
          Needs work
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Input type="number" min={0} max={maxScore} value={score}
          onChange={e => setScore(e.target.value)} placeholder={`0-${maxScore}`}
          data-testid={`grade-score-${sub.id}`} />
        <Input className="col-span-2" value={feedback}
          onChange={e => setFeedback(e.target.value)} placeholder="Feedback (optional)"
          data-testid={`grade-feedback-${sub.id}`} />
        <Button size="sm" onClick={() => onGrade(score, feedback)}
          className="rounded-full font-semibold"
          data-testid={`grade-save-${sub.id}`}>
          <Award className="w-3.5 h-3.5 mr-1.5" /> Save grade
        </Button>
      </div>
    </div>
  );
};

/* ------------------ DISCUSSION ------------------ */
export const DiscussionPanel = ({ lessonId }) => {
  const { API, user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const fetchPosts = async () => {
    try {
      const r = await axios.get(`${API}/lms/lessons/${lessonId}/discussions`);
      setPosts(r.data || []);
    } catch { setPosts([]); }
  };
  useEffect(() => { fetchPosts(); }, [lessonId]);

  const submit = async () => {
    if (!content.trim()) { toast.error('Type a message first'); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/lms/lessons/${lessonId}/discussions`, {
        content, parent_id: replyTo,
      });
      setContent(''); setReplyTo(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    } finally { setPosting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/lms/discussions/${id}`);
      fetchPosts();
    } catch { toast.error('Not allowed'); }
  };

  const tree = useMemo(() => {
    const byParent = {};
    posts.forEach(p => {
      const k = p.parent_id || '__root__';
      byParent[k] = byParent[k] || [];
      byParent[k].push(p);
    });
    return byParent;
  }, [posts]);

  const renderThread = (parentId = '__root__', depth = 0) => {
    const list = tree[parentId] || [];
    return list.map(p => (
      <div key={p.id} className={depth ? 'ml-6 pl-3 border-l border-border mt-2' : 'mt-3'}
        data-testid={`discussion-post-${p.id}`}>
        <div className="rounded-lg border border-border p-3 bg-card">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span className="font-semibold text-foreground">
              {p.user_email} {p.user_role === 'admin' && <span className="status-pill status-pill-admin ml-1.5">admin</span>}
            </span>
            <span>{new Date(p.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-foreground/90 whitespace-pre-wrap">{p.content}</p>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
              onClick={() => setReplyTo(p.id)} data-testid={`discussion-reply-${p.id}`}>
              <Reply className="w-3 h-3 mr-1" /> Reply
            </Button>
            {(p.user_id === user?.id || user?.role === 'admin') && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive"
                onClick={() => remove(p.id)} data-testid={`discussion-delete-${p.id}`}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
        {renderThread(p.id, depth + 1)}
      </div>
    ));
  };

  return (
    <Card className="mb-6" data-testid="discussion-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-primary" /> Discussion
          <span className="text-xs font-normal text-muted-foreground ml-1">({posts.filter(p => !p.is_deleted).length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          {replyTo && (
            <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
              Replying to a comment
              <button className="underline" onClick={() => setReplyTo(null)}>cancel</button>
            </div>
          )}
          <Textarea rows={3} value={content} onChange={e => setContent(e.target.value)}
            placeholder="Share what you learned or ask a question..."
            data-testid="discussion-input" />
          <div className="mt-2">
            <Button size="sm" onClick={submit} disabled={posting}
              className="rounded-full font-semibold" data-testid="discussion-post-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Post
            </Button>
          </div>
        </div>
        {posts.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No discussion yet — be the first to post.</p>
        )}
        {renderThread('__root__', 0)}
      </CardContent>
    </Card>
  );
};
